/**
 * SPDX-FileCopyrightText: 2025 Nextcloud GmbH and Nextcloud contributors
 * SPDX-License-Identifier: AGPL-3.0-or-later
 */

/* eslint-disable no-console */

import { create } from 'zustand'
import type { CollaborationSocket } from '../types/collaboration'
import type { Voting } from '../types/voting'

export type CollaborationConnectionStatus = 'online' | 'offline' | 'connecting' | 'reconnecting'

export type AuthErrorType = 'jwt_secret_mismatch' | 'token_expired' | 'unauthorized' | null

interface AuthErrorState {
	type: AuthErrorType
	message: string | null
	consecutiveFailures: number
	lastFailureTime: number | null
	isPersistent: boolean // True when we've detected a persistent auth issue (likely JWT secret mismatch)
}

interface CollaborationStore {
	status: CollaborationConnectionStatus
	socket: CollaborationSocket | null
	isDedicatedSyncer: boolean // Is this client responsible for syncing to server/broadcasting?
	authError: AuthErrorState
	followedUserId: string | null // User ID being followed for viewport synchronization
	isInRoom: boolean // Whether the socket has joined the current room

	// Presentation state
	presenterId: string | null // User ID of current presenter
	isPresentationMode: boolean // Whether presentation mode is active in the room
	isPresenting: boolean // Whether current user is presenting
	presentationStartTime: number | null // When presentation started
	autoFollowPresenter: boolean // Whether to automatically follow presenter (can be disabled by user)

	votings: Voting[]

	// Actions
	setStatus: (status: CollaborationConnectionStatus) => void
	setSocket: (socket: CollaborationSocket | null) => void
	setDedicatedSyncer: (isSyncer: boolean) => void
	setIsInRoom: (inRoom: boolean) => void
	setAuthError: (error: Partial<AuthErrorState>) => void
	incrementAuthFailure: (errorType: AuthErrorType, message: string) => void
	clearAuthError: () => void
	resetStore: () => void

	// Presentation actions
	setPresentationState: (state: {
		presenterId?: string | null
		isPresentationMode?: boolean
		isPresenting?: boolean
		presentationStartTime?: number | null
	}) => void
	setAutoFollowPresenter: (autoFollow: boolean) => void

	// Voting actions
	addVoting: (voting: Voting) => void
	updateVoting: (voting: Voting) => void
	setVotings: (votings: Voting[]) => void
}

const initialAuthErrorState: AuthErrorState = {
	type: null,
	message: null,
	consecutiveFailures: 0,
	lastFailureTime: null,
	isPersistent: false,
}

const initialState: Omit<CollaborationStore, 'setStatus' | 'setSocket' | 'setDedicatedSyncer' | 'setAuthError' | 'incrementAuthFailure' | 'clearAuthError' | 'resetStore' | 'setPresentationState' | 'setAutoFollowPresenter' | 'addVoting' | 'updateVoting' | 'setVotings'> = {
	status: 'offline',
	socket: null,
	isDedicatedSyncer: false,
	authError: initialAuthErrorState,
	followedUserId: null,
	isInRoom: false,

	// Presentation initial state
	presenterId: null,
	isPresentationMode: false,
	isPresenting: false,
	presentationStartTime: null,
	autoFollowPresenter: true,

	votings: [],
}

// Constants for auth failure detection
const MAX_AUTH_FAILURES = 3
const PERSISTENT_FAILURE_THRESHOLD = 5 * 60 * 1000 // 5 minutes

export const useCollaborationStore = create<CollaborationStore>()((set) => ({
	...initialState,

	setStatus: (status) => set((state) => (state.status === status ? {} : { status })),
	setSocket: (socket) => set({ socket }),
	setDedicatedSyncer: (isSyncer) => set({ isDedicatedSyncer: isSyncer }),
	setIsInRoom: (inRoom) => set({ isInRoom: inRoom }),

	setAuthError: (error) => set((state) => ({
		authError: { ...state.authError, ...error },
	})),

	incrementAuthFailure: (errorType, message) => set((state) => {
		const now = Date.now()
		const newFailureCount = state.authError.consecutiveFailures + 1

		// Determine if this is a persistent issue (likely JWT secret mismatch)
		const isPersistent = newFailureCount >= MAX_AUTH_FAILURES
			&& (state.authError.lastFailureTime === null
			 || now - state.authError.lastFailureTime < PERSISTENT_FAILURE_THRESHOLD)

		return {
			authError: {
				type: errorType,
				message,
				consecutiveFailures: newFailureCount,
				lastFailureTime: now,
				isPersistent,
			},
		}
	}),

	clearAuthError: () => set({ authError: initialAuthErrorState }),

	resetStore: () => set(initialState),

	// Presentation actions
	setPresentationState: (state) => set((currentState) => ({
		...currentState,
		...state,
	})),

	setAutoFollowPresenter: (autoFollow) => set({ autoFollowPresenter: autoFollow }),

	// Voting actions
	addVoting: (voting) => set((state) => ({
		votings: [...state.votings, voting],
	})),

	updateVoting: (voting) => set((state) => ({
		votings: state.votings.map(v => v.uuid === voting.uuid ? voting : v),
	})),

	setVotings: (votings) => set({ votings }),
}))
